import { ConversionFamily, StaffPerformanceRecord, StaffPeriods, StaffSuccessUpdates, SuccessUpdateCounts } from '../types';
import { countSegments } from './conversionSegments';
import {
  Delta,
  TeamAggregates,
  countDelta,
  formatDecimal,
  formatInt,
  periodLabel,
  ptsDelta,
  signed,
  signedPct,
} from './metrics';
import { EMPTY_SUCCESS_COUNTS, overdueShare } from './successUpdates';

export type KpiTileKey = 'sessions' | 'clients' | 'conversion' | 'updates';
export type KpiTileColor = 'primary' | 'secondary' | 'success' | 'warning';

/** Everything a KPI tile needs to render; built here so the copy is unit-tested. */
export interface KpiTileModel {
  key: KpiTileKey;
  label: string;
  value: string;
  delta: Delta | null;
  /** Text that follows the delta, e.g. "(−8.5%) vs prior 6 months". */
  deltaSuffix: string;
  caption: string;
  color: KpiTileColor;
  spark?: { counts: number[]; latestFrom: number };
}

const LABELS: Record<KpiTileKey, string> = {
  sessions: 'Sessions per month',
  clients: 'Net client change',
  conversion: 'First programme conversion',
  updates: 'Overdue success updates',
};
const COLORS: Record<KpiTileKey, KpiTileColor> = {
  sessions: 'primary',
  clients: 'secondary',
  conversion: 'success',
  updates: 'warning',
};
const PRIOR = 'vs prior 6 months';

/** Whole numbers without a decimal, everything else with one. */
const plain = (value: number | null | undefined): string =>
  typeof value !== 'number' || !Number.isFinite(value) ? 'n/a' : Number.isInteger(value) ? formatInt(value) : formatDecimal(value);

const pct = (value: number | null | undefined): string =>
  typeof value !== 'number' || !Number.isFinite(value) ? 'n/a' : `${formatDecimal(value)}%`;

function naTile(key: KpiTileKey, caption = 'No data for this period', label = LABELS[key]): KpiTileModel {
  return { key, label, value: 'n/a', delta: null, deltaSuffix: '', caption, color: COLORS[key] };
}

/**
 * "1 finished but did not rebuy · 2 dropped off · " for the latest period, counted from the client list; empty for
 * documents synced without one, which keeps their caption as it was.
 */
function nonConversionSegments(conversion: ConversionFamily): string {
  if (conversion.clients.length === 0) return '';
  const { finished, dropped } = countSegments(conversion.clients, 'latest_6m');
  return `${finished} finished but did not rebuy · ${dropped} dropped off · `;
}

/** "No change" when equal, otherwise the absolute difference as a whole number. */
function netDelta(latestNet: number, priorNet: number): { delta: Delta; deltaSuffix: string } {
  const difference = latestNet - priorNet;
  return {
    delta: difference === 0 ? { direction: 'flat', text: 'No change' } : countDelta(difference, 0),
    deltaSuffix: `${PRIOR} (${signed(priorNet)})`,
  };
}

/**
 * One staff member's tiles. Pass `updates` only once the client-update figures are ready: `undefined` keeps the
 * three performance tiles, while a document (or `null` for a coach with no active clients) adds the overdue tile.
 */
export function buildDetailTiles(record: StaffPerformanceRecord, updates?: StaffSuccessUpdates | null): KpiTileModel[] {
  const { sessions, clients, conversion } = record;

  const sessionsTile: KpiTileModel = sessions
    ? {
        key: 'sessions',
        label: LABELS.sessions,
        color: COLORS.sessions,
        value: sessions.latest.mean === null ? 'n/a' : formatDecimal(sessions.latest.mean),
        delta: sessions.meanChange === null ? null : countDelta(sessions.meanChange),
        deltaSuffix:
          sessions.meanChange === null
            ? ''
            : sessions.meanChangePct === null
            ? PRIOR
            : `(${signedPct(sessions.meanChangePct)}) ${PRIOR}`,
        caption: `${formatInt(sessions.latest.total)} sessions over ${sessions.latest.activeMonths} active months · median ${plain(
          sessions.latest.median
        )} · prior mean ${plain(sessions.prior.mean)}`,
        spark: {
          counts: sessions.monthly.map((m) => m.count),
          latestFrom: Math.max(
            0,
            sessions.monthly.findIndex((m) => m.period === 'latest_6m') === -1
              ? sessions.monthly.length
              : sessions.monthly.findIndex((m) => m.period === 'latest_6m')
          ),
        },
      }
    : naTile('sessions');

  const clientsTile: KpiTileModel = clients
    ? {
        key: 'clients',
        label: LABELS.clients,
        color: COLORS.clients,
        value: signed(clients.latest.net),
        ...netDelta(clients.latest.net, clients.prior.net),
        caption: `${clients.latest.new} new clients · ${clients.latest.nonReturning} non-returning · prior ${clients.prior.new} new, ${clients.prior.nonReturning} non-returning`,
      }
    : naTile('clients');

  const conversionTile: KpiTileModel = conversion
    ? {
        key: 'conversion',
        label: LABELS.conversion,
        color: COLORS.conversion,
        value: pct(conversion.latest.ratePct),
        delta: conversion.rateChangePts === null ? null : ptsDelta(conversion.rateChangePts),
        deltaSuffix:
          conversion.prior.ratePct === null ? `${PRIOR} (no expiries)` : `${PRIOR} (${pct(conversion.prior.ratePct)})`,
        caption: `${conversion.latest.converted} of ${conversion.latest.expired} first packages converted · ${nonConversionSegments(
          conversion
        )}all-time ${pct(conversion.allTime.ratePct)} (${conversion.allTime.converted} of ${conversion.allTime.expired})`,
      }
    : naTile('conversion');

  const tiles = [sessionsTile, clientsTile, conversionTile];
  return updates === undefined ? tiles : [...tiles, buildUpdatesTile(updates?.counts ?? EMPTY_SUCCESS_COUNTS, 'No active clients')];
}

const coaches = (count: number): string => `${count} ${count === 1 ? 'coach' : 'coaches'}`;

/**
 * The 4th tile. `null` = the updates have not been checked yet; counts with no active clients read n/a too,
 * captioned `emptyCaption`. Awaiting-first clients are shown beside the overdue count, never inside it.
 */
function buildUpdatesTile(counts: SuccessUpdateCounts | null, emptyCaption = 'No active clients in this scope'): KpiTileModel {
  if (counts === null) return naTile('updates', 'Client updates have not been checked yet');
  const share = overdueShare(counts);
  if (share === null) return naTile('updates', emptyCaption);

  const clientsWord = counts.active === 1 ? 'client' : 'clients';
  return {
    key: 'updates',
    label: LABELS.updates,
    color: COLORS.updates,
    value: formatInt(counts.overdue),
    delta: null,
    deltaSuffix: '',
    caption: `${share}% of ${formatInt(counts.active)} active ${clientsWord} · ${formatInt(
      counts.awaiting_first
    )} awaiting a first update · ${formatInt(counts.due_soon)} due in the next 7 days`,
  };
}

export function buildTeamTiles(
  agg: TeamAggregates,
  periods: StaffPeriods | null,
  updates?: SuccessUpdateCounts | null
): KpiTileModel[] {
  const sessionsLabel = `Team sessions · ${periodLabel(periods?.latest)}`;
  const sessionsTile: KpiTileModel = {
    key: 'sessions',
    label: sessionsLabel,
    color: COLORS.sessions,
    value: formatInt(agg.sessionsLatest),
    delta: countDelta(agg.sessionsLatest - agg.sessionsPrior, 0),
    deltaSuffix:
      agg.sessionsChangePct === null
        ? `${PRIOR} (${formatInt(agg.sessionsPrior)})`
        : `(${signedPct(agg.sessionsChangePct)}) ${PRIOR} (${formatInt(agg.sessionsPrior)})`,
    caption: `${agg.activeCount} of ${agg.staffCount} staff active · ${plain(
      agg.meanPerActiveCoachLatest
    )} sessions per active coach per month (prior ${plain(agg.meanPerActiveCoachPrior)})`,
  };

  const clientsTile: KpiTileModel =
    agg.coachesWithClients === 0
      ? naTile('clients', 'No client-change data in this scope')
      : {
          key: 'clients',
          label: LABELS.clients,
          color: COLORS.clients,
          value: signed(agg.netLatest),
          ...netDelta(agg.netLatest, agg.netPrior),
          caption: `${agg.newLatest} new clients · ${agg.nonReturningLatest} non-returning · ${coaches(
            agg.coachesWithClients
          )} with client data`,
        };

  const conversionTile: KpiTileModel =
    agg.coachesWithConversion === 0
      ? naTile('conversion', 'No conversion data in this scope')
      : {
          key: 'conversion',
          label: LABELS.conversion,
          color: COLORS.conversion,
          value: pct(agg.rateLatest),
          delta: agg.rateChangePts === null ? null : ptsDelta(agg.rateChangePts),
          deltaSuffix: agg.ratePrior === null ? `${PRIOR} (no expiries)` : `${PRIOR} (${pct(agg.ratePrior)})`,
          caption: `${agg.convertedLatest} of ${agg.expiredLatest} first packages converted · ${coaches(
            agg.coachesWithConversion
          )} with conversion data`,
        };

  const tiles = [sessionsTile, clientsTile, conversionTile];
  return updates === undefined ? tiles : [...tiles, buildUpdatesTile(updates)];
}
